
import React from "react";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";
const LOGO_URL = "https://static.readdy.ai/image/2ff91b078c346dc7f0835d5078a50dee/d39871e2a6658ac59cb024a68c5f9daa.png";

const ORG_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;
const SERVICE_ID = `${SITE_URL}/#service`;

/**
 * Injecte du JSON-LD dans le HTML rendu (meilleur que useEffect).
 */
export const JsonLd: React.FC<{ data: object | object[] }> = ({ data }) => (
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
  />
);

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORG_ID,
  name: "Voicely",
  url: SITE_URL,
  logo: LOGO_URL,
  image: LOGO_URL,
  description: "Voicely est une solution intelligente conçue pour les restaurants qui ne peuvent pas répondre à chaque appel, notamment en période de service.",
  contactPoint: {
    "@type": "ContactPoint",
    email: "voicely.ia@gmail.com",
    contactType: "Service client",
    availableLanguage: ["fr", "en"]
  }
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  url: SITE_URL,
  name: "Voicely",
  description: "Assistant IA pour votre service client",
  publisher: { "@id": ORG_ID },
  inLanguage: "fr"
};

export const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": SERVICE_ID,
  name: "Voicely - Assistant IA pour service client",
  serviceType: "Assistant IA pour service client",
  description: "Voicely est une solution intelligente conçue pour les restaurants qui ne peuvent pas répondre à chaque appel, notamment en période de service. Elle prend en charge uniquement les appels manqués et assure un suivi client fluide, directement sur WhatsApp, tout en vous laissant le contrôle sur vos réservations.",
  provider: { "@id": ORG_ID },
  areaServed: { "@type": "Country", name: "France" },
  availableLanguage: ["fr", "en"],
  audience: {
    "@type": "Audience",
    audienceType: "Restaurants, Bars, Clubs et Lieux festifs"
  },
  offers: {
    "@type": "Offer",
    availability: "https://schema.org/InStock",
    availabilityStarts: "2025-01-01"
  }
};

export const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Comment fonctionne Voicely ?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Lorsqu'un client appelle et que vous ne pouvez pas répondre, il laisse un message vocal. Notre IA analyse instantanément ce message, comprend la demande et répond au client directement via WhatsApp en quelques secondes. Vous gardez le contrôle total depuis votre tableau de bord."
      }
    },
    {
      "@type": "Question",
      name: "Voicely est-il disponible 24h/24 ?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Oui, Voicely fonctionne 24h/24 et 7j/7. Votre assistant IA ne dort jamais et peut gérer les demandes de vos clients à tout moment, même en dehors de vos horaires d'ouverture."
      }
    },
    {
      "@type": "Question",
      name: "Quels types d'établissements peuvent utiliser Voicely ?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Voicely est conçu spécialement pour les restaurants, bars, clubs et lieux festifs. Notre solution s'adapte parfaitement aux besoins de la restauration et de l'hôtellerie."
      }
    },
    {
      "@type": "Question",
      name: "Voicely peut-il communiquer en plusieurs langues ?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Oui, votre assistant communique avec vos clients par téléphone et WhatsApp en anglais ou en français, s'adaptant automatiquement à la langue de votre client."
      }
    },
    {
      "@type": "Question",
      name: "Comment puis-je personnaliser Voicely pour mon établissement ?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Voicely est entièrement personnalisable en quelques clics. Vous pouvez adapter l'assistant à votre image, vos processus et vos besoins spécifiques. De plus, Voicely se connecte à votre plateforme de gestion habituelle (ex. Zenchef, TheFork, ou tout autre système de réservation) afin de récupérer et mettre à jour les informations en temps réel."
      }
    }
  ]
};
